import ChartMetric from '@/Enums/Chart/Metric.enum';
import Color from '@/Enums/Color.enum';
import DateFormat from '@/Enums/Format/DateFormat.enum';
import { BeeswaxReportProperties as ReportProperties } from '@/Enums/Report/BeeswaxReportProperties.enum';
import type { IFootTrafficWeekInsightRequest } from '@/Interfaces/Attribution/FootTrafficWeekInsight.interface';
import FootTrafficService from '@/Services/FootTraffic/FootTraffic.service';
import { getUsHolidaysInRange } from '@/helpers/attribution/usHolidays';
import { downloadTableCsv } from '@/helpers/downloadCSV';
import { useAppSelector } from '@/store/hooks';
import {
  selectFootTrafficDateReport,
  selectFootTrafficFilters,
  selectFootTrafficTotalsChannels,
  selectFootTrafficVisitsHistory,
} from '@/store/selectors/attribution/footTraffic.selectors';
import { FormControlLabel, Switch, Typography } from '@mui/material';
import {
  BarElement,
  CategoryScale,
  ChartData,
  Chart as ChartJS,
  ChartOptions,
  Legend,
  LineController,
  LineElement,
  LinearScale,
  PointElement,
  Tooltip,
} from 'chart.js';
import annotationPlugin from 'chartjs-plugin-annotation';
import moment from 'moment';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Chart } from 'react-chartjs-2';
import GraphCard from './BaseCard/GraphCard';
import WeekInsightPanel from './WeekInsightPanel';
import YoyChangeStrip from './YoyChangeStrip';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  LineController,
  annotationPlugin,
);

interface CustomChartOptions extends ChartOptions<'bar'> {
  maxBarThickness?: number;
  borderRadius?: number;
  borderSkipped?: boolean;
}

const legendOptions = {
  impressionsOmnichannel: { label: ChartMetric.ImpressionsOmnichannel, color: Color.Primary },
  impressionsYoutube: { label: ChartMetric.ImpressionsYoutube, color: Color.LightGreen },
  impressionsAudio: { label: ChartMetric.ImpressionsAudio, color: Color.Orange },
  visits: { label: ChartMetric.FootTrafficVisits, color: Color.Orange },
  priorYearVisits: { label: 'Prior Year Visits', color: `${Color.Orange}80` },
  historicalAvgVisits: { label: '3-Year Avg Visits', color: Color.Grey },
};

const chartOptions: CustomChartOptions = {
  responsive: true,
  plugins: {
    datalabels: { display: false },
    legend: {
      position: 'top',
      align: 'end',
      labels: { boxHeight: 12, boxWidth: 12, padding: 12 },
    },
  },
  maxBarThickness: 47,
  borderRadius: 0,
  borderSkipped: false,
  backgroundColor: '#193B2C',
  interaction: {
    intersect: false,
    mode: 'index',
  },
  scales: {
    y: {
      title: {
        display: true,
        text: legendOptions.impressionsOmnichannel.label,
        align: 'center',
        color: legendOptions.impressionsOmnichannel.color,
      },
      grid: { drawTicks: false },
      border: { display: false, dash: [6, 3] },
      stacked: true,
    },
    y1: {
      title: {
        display: true,
        text: legendOptions.visits.label,
        align: 'center',
        color: legendOptions.visits.color,
      },
      position: 'right',
      grid: { drawTicks: false, drawOnChartArea: false },
    },
    x: { ticks: { autoSkip: false, color: Color.Grey }, stacked: true },
  },
  maintainAspectRatio: false,
};

const label = 'Impressions & Visits';

const TooltipContent = () => (
  <div className='space-y-2'>
    <p className='font-bold text-[14px]'>Foot Traffic Analytics Disclaimer</p>
    <p>
      The foot traffic insights results provided by this product are estimates based on a combination of probabilistic
      and deterministic methodologies that match advertising exposure data with mobile location signals. These results
      are intended for directional insight only and should not be interpreted as precise measurements of individual
      consumer behavior.
    </p>
    <p>
      The presence or absence of measured lift in foot traffic is not a guarantee of actual campaign effectiveness,
      sales impact, or return on investment. Variability in data sources, privacy protections, publisher/device
      coverage, and consumer mobility—as well as external market conditions such as weather, competitive activity,
      advertiser promotional activity, and seasonality—may affect the accuracy and completeness of reported outcomes.
    </p>
    <p>
      All insights outputs are provided “as is” without warranties of any kind, and Ribeye expressly disclaims any
      liability for decisions made based on these results. Advertisers should use insights reporting as one of multiple
      tools in evaluating campaign performance and should supplement these insights with additional data sources
      whenever possible.
    </p>
  </div>
);

const FootTrafficVisitsCard = () => {
  const { dateReport, loading, flightWindows } = useAppSelector(selectFootTrafficDateReport);
  const channels = useAppSelector(selectFootTrafficTotalsChannels);
  const filters = useAppSelector(selectFootTrafficFilters);
  const { visitsHistory } = useAppSelector(selectFootTrafficVisitsHistory);

  const [chartData, setChartData] = useState<ChartData>({ labels: [], datasets: [{ data: [] }] });
  const [showHolidays, setShowHolidays] = useState(true);
  const [showFlights, setShowFlights] = useState(true);
  const [insightPayload, setInsightPayload] = useState<IFootTrafficWeekInsightRequest | null>(null);

  const advertiserId = filters.advertisers[0]?.value as number | undefined;

  const handleWeekClick = useCallback(
    (dayIndex: number) => {
      const clicked = dateReport[dayIndex];
      if (!clicked || advertiserId == null) return;
      const weekStart = moment(clicked.Day).startOf('isoWeek');
      const weekEnd = weekStart.clone().add(6, 'days');
      const weekDays = dateReport.filter(d => {
        const day = moment(d.Day);
        return day.isSameOrAfter(weekStart, 'day') && day.isSameOrBefore(weekEnd, 'day');
      });
      const visits = weekDays.reduce((s, d) => s + (d['Foot Traffic Visits'] ?? 0), 0);
      const impressions = weekDays.reduce(
        (s, d) =>
          s + (d['Omnichannel Impressions'] ?? 0) + (d['YouTube Impressions'] ?? 0) + (d['Audio Impressions'] ?? 0),
        0,
      );
      const priorYearWeekKey = weekStart.clone().subtract(1, 'year').format('YYYY-MM-DD');
      const priorYearVisits = visitsHistory.find(h => h.weekStartDate === priorYearWeekKey)?.visits ?? undefined;

      setInsightPayload({
        advertiserId,
        weekStartDate: weekStart.format('YYYY-MM-DD'),
        locationIds: (filters.locations ?? []).map(l => Number(l.value)).filter(n => !Number.isNaN(n)),
        weekTotals: { impressions, visits, priorYearVisits },
        campaignNames: (filters.campaigns ?? []).map(c => c.label),
      });
    },
    [dateReport, visitsHistory, filters.locations, filters.campaigns, advertiserId],
  );

  const holidays = useMemo(
    () => (showHolidays ? getUsHolidaysInRange(filters.dateRangeStart, filters.dateRangeEnd) : []),
    [filters.dateRangeStart, filters.dateRangeEnd, showHolidays],
  );

  const flightBands = useMemo(() => {
    if (!showFlights || !flightWindows.length || !dateReport.length) return [];
    const firstDay = dateReport[0][ReportProperties.DAY];
    const lastDay = dateReport[dateReport.length - 1][ReportProperties.DAY];
    return flightWindows
      .map(w => ({
        // Clamp to the chart's visible date range
        start: w.start < firstDay ? firstDay : w.start,
        end: w.end > lastDay ? lastDay : w.end,
        label: w.label,
      }))
      .filter(w => w.start <= w.end);
  }, [flightWindows, dateReport, showFlights]);

  const annotations = useMemo(() => {
    const holidayAnnotations = Object.fromEntries(
      holidays.map((h, i) => {
        const hlabel = moment(h.date).format(DateFormat.ShortMonthDay);
        return [
          `holiday-${i}`,
          {
            type: 'line' as const,
            xMin: hlabel,
            xMax: hlabel,
            borderColor: `${Color.Grey}99`,
            borderWidth: 1,
            borderDash: [4, 4],
            label: {
              content: h.name,
              display: true,
              position: 'start' as const,
              backgroundColor: 'rgba(255, 255, 255, 0.85)',
              color: `${Color.Grey}e6`,
              font: { size: 10 },
              padding: 2,
            },
          },
        ];
      }),
    );
    const flightAnnotations = Object.fromEntries(
      flightBands.map((b, i) => [
        `flight-${i}`,
        {
          type: 'box' as const,
          xMin: moment(b.start).format(DateFormat.ShortMonthDay),
          xMax: moment(b.end).format(DateFormat.ShortMonthDay),
          backgroundColor: `${Color.Primary}1a`,
          borderColor: `${Color.Primary}40`,
          borderWidth: 1,
          drawTime: 'beforeDatasetsDraw' as const,
          label: {
            content: b.label,
            display: true,
            position: 'start' as const,
            backgroundColor: `${Color.Primary}cc`,
            color: '#fff',
            font: { size: 10 },
            padding: 2,
          },
        },
      ]),
    );
    return { ...flightAnnotations, ...holidayAnnotations };
  }, [holidays, flightBands]);

  const chartOptionsWithAnnotations = useMemo(
    () => ({
      ...chartOptions,
      plugins: {
        ...chartOptions.plugins,
        annotation: { annotations },
      },
      onClick: (_evt: unknown, elements: { index: number }[]) => {
        if (elements?.length) handleWeekClick(elements[0].index);
      },
    }),
    [annotations, handleWeekClick],
  );

  const noData = useMemo<boolean>(() => !dateReport.length, [dateReport]);

  useEffect(() => {
    const {
      sortedDates,
      omnichannelImpressions,
      youtubeImpressions,
      audioImpressions,
      visits,
      priorYearVisits,
      historicalAvgVisits,
      hasPriorYearOverlay,
      hasHistoricalAvgOverlay,
    } = FootTrafficService.getChartDisplayData(dateReport, channels, visitsHistory);

    const datasets: ChartData<'bar' | 'line'>['datasets'] = [
      {
        type: 'line',
        label: legendOptions.visits.label,
        data: visits,
        borderColor: legendOptions.visits.color,
        backgroundColor: legendOptions.visits.color,
        borderWidth: 3,
        fill: false,
        yAxisID: 'y1',
      },
      {
        type: 'bar',
        label: legendOptions.impressionsOmnichannel.label,
        data: omnichannelImpressions,
        backgroundColor: legendOptions.impressionsOmnichannel.color,
        yAxisID: 'y',
      },
      {
        type: 'bar',
        label: legendOptions.impressionsYoutube.label,
        data: youtubeImpressions,
        backgroundColor: legendOptions.impressionsYoutube.color,
        yAxisID: 'y',
      },
      {
        type: 'bar',
        label: legendOptions.impressionsAudio.label,
        data: audioImpressions,
        backgroundColor: legendOptions.impressionsAudio.color,
        yAxisID: 'y',
      },
    ];

    if (hasPriorYearOverlay) {
      datasets.push({
        type: 'line',
        label: legendOptions.priorYearVisits.label,
        data: priorYearVisits,
        borderColor: legendOptions.priorYearVisits.color,
        backgroundColor: legendOptions.priorYearVisits.color,
        borderWidth: 2,
        borderDash: [6, 3],
        fill: false,
        pointRadius: 0,
        spanGaps: true,
        yAxisID: 'y1',
      });
    }

    if (hasHistoricalAvgOverlay) {
      datasets.push({
        type: 'line',
        label: legendOptions.historicalAvgVisits.label,
        data: historicalAvgVisits,
        borderColor: legendOptions.historicalAvgVisits.color,
        backgroundColor: legendOptions.historicalAvgVisits.color,
        borderWidth: 2,
        borderDash: [2, 4],
        fill: false,
        pointRadius: 0,
        spanGaps: true,
        yAxisID: 'y1',
      });
    }

    setChartData({ labels: sortedDates, datasets });
  }, [dateReport, channels, visitsHistory]);

  const onDownload = useCallback(() => {
    const downloadCSVData = FootTrafficService.getChartCsv(dateReport, visitsHistory);
    downloadTableCsv(downloadCSVData, label);
  }, [dateReport, visitsHistory]);

  return (
    <GraphCard
      label={label}
      loading={loading}
      noData={noData}
      onDownload={onDownload}
      headerTooltipValue={<TooltipContent />}
    >
      <div className='w-full'>
        <FormControlLabel
          control={<Switch checked={showHolidays} onChange={e => setShowHolidays(e.target.checked)} size='small' />}
          label='Holidays'
          sx={{ ml: 0, mb: 1, '& .MuiFormControlLabel-label': { fontSize: 12, color: 'text.secondary' } }}
        />
        {flightWindows.length > 0 && (
          <FormControlLabel
            control={<Switch checked={showFlights} onChange={e => setShowFlights(e.target.checked)} size='small' />}
            label='Campaign Flights'
            sx={{ ml: 1, mb: 1, '& .MuiFormControlLabel-label': { fontSize: 12, color: 'text.secondary' } }}
          />
        )}
        {visitsHistory.length > 0 && (
          <Typography variant='caption' sx={{ display: 'block', color: 'text.secondary', mb: 1, fontStyle: 'italic' }}>
            Prior-year and 3-year average aligned by ISO-week offset from {filters.dateRangeStart}
          </Typography>
        )}
        <Typography variant='caption' sx={{ display: 'block', color: 'text.secondary', mb: 1 }}>
          Tip: click any week on the chart for a deeper AI insight.
        </Typography>
        <div style={{ position: 'relative', height: 283, width: '100%', cursor: 'pointer' }}>
          <Chart type='bar' options={chartOptionsWithAnnotations} data={chartData as ChartData<'bar'>} />
        </div>
        <YoyChangeStrip
          dateRangeStart={filters.dateRangeStart}
          dateRangeEnd={filters.dateRangeEnd}
          visitsHistory={visitsHistory}
        />
      </div>
      <WeekInsightPanel open={!!insightPayload} onClose={() => setInsightPayload(null)} payload={insightPayload} />
    </GraphCard>
  );
};

export default FootTrafficVisitsCard;
